import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import CardHeader from '../../components/CardHeader';
import ArrowRightIcon from '../../assets/icons/ArrowRightIcon';
import RightArrowIcon from '../../assets/icons/RightArrowIcon';
import UpLoadingBar from '../../components/Common/UpLoadingBar';
import UpLoadingCover from '../../components/Common/UpLoadingCover';
import { getAvailablePlatforms } from '../../helpers/bizDataHelpers';
import LocalStorageHelper from '../../helpers/localStorageHelpers';
import usePlatformRedirect from '../../helpers/usePlatformRedirect';

function SelectPlatform() {
  const { bizId } = useParams();
  const navigate = useNavigate();
  const { selectedBiz, platforms } = getAvailablePlatforms(bizId);
  const redirectHost = LocalStorageHelper.getRedirectHost();

  const {
    isLoading,
    redirect: callRedirectPlatform,
    atlasEnvPicker,
  } = usePlatformRedirect();

  function redirectToBizPlatform(platform) {
    if (platform?.multiBiz) {
      navigate(`/business/${bizId}/${platform?.id}`);
      return;
    }

    callRedirectPlatform({
      appType: platform?.id,
      params: {
        biz_id: Number(bizId),
        app_name: platform?.id,
        app_biz_id: platform?.bizId,
        redirect_host: redirectHost,
      },
    });
  }

  // MCP logins already targeted a single app (Atlas), so skip the app picker and
  // redirect straight through instead of making the user click the one card.
  const isMcpLogin = redirectHost?.startsWith('mcp-');
  const renderablePlatforms = platforms?.filter(p => p?.bizId);
  const autoRedirect = isMcpLogin && renderablePlatforms?.length === 1;

  useEffect(() => {
    if (autoRedirect) {
      redirectToBizPlatform(renderablePlatforms[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!selectedBiz || platforms?.length === 0) {
    return <Navigate to={'/business'} />;
  }

  if (autoRedirect) {
    return (
      <div className="light-view-wrapper relative">
        {atlasEnvPicker}
        <UpLoadingBar loading />
        <UpLoadingCover loading />
      </div>
    );
  }

  return (
    <div>
      {atlasEnvPicker}
      <UpLoadingBar loading={isLoading} />
      <div className="light-view-wrapper relative">
        <UpLoadingCover loading={isLoading} />
        <button
          onClick={() => navigate('/business')}
          aria-label={`Back to business selection (${selectedBiz})`}
          className="text-sm text-up-clr-light-grey mb-1 flex items-center cursor-pointer bg-transparent border-0 p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-up-clr-primary focus-visible:ring-offset-2 rounded">
          <span className="mr-1 capitalize">{selectedBiz}</span>
          <RightArrowIcon />
        </button>
        <CardHeader title="Select an app" wrapperClass="pb-6" />
        <ul role="list" aria-label="Available apps">
          {platforms?.map((platform, idx, pArr) => {
            if (!platform?.bizId) {
              return null;
            }

            return (
              <li key={platform?.id}>
                <PlatformCard
                  platform={platform}
                  redirectToBizPlatform={redirectToBizPlatform}
                />
                {idx < pArr.length - 1 && (
                  <div aria-hidden="true" className="border-t border-up-clr-neutral-grey md:-mx-8" />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function PlatformCard({ platform, redirectToBizPlatform }) {
  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      redirectToBizPlatform(platform);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${platform.name}${platform.desc ? `. ${platform.desc}` : ''}`}
      onClick={() => redirectToBizPlatform(platform)}
      onKeyDown={handleKeyDown}
      className="p-1 my-5 flex items-center cursor-pointer transition-colors duration-500 text-up-clr-neutral-grey hover:text-up-clr-tinted-grey focus:outline-none focus-visible:ring-2 focus-visible:ring-up-clr-primary focus-visible:ring-offset-2 rounded">
      <div aria-hidden="true" className="w-12 h-12 flex-shrink-0 self-start">
        <img
          src={platform.logo}
          alt=""
          className="w-full h-full object-contain"
        />
      </div>
      <div style={{ maxWidth: 300 }} className="ml-4 mr-auto">
        <p aria-hidden="true" className="text-lg font-semibold text-up-clr-black leading-6">
          {platform.name}
        </p>
        <p aria-hidden="true" className="text-sm text-up-clr-dark-grey">{platform.desc}</p>
      </div>
      <div aria-hidden="true" className="p-2 select-none">
        <ArrowRightIcon />
      </div>
    </div>
  );
}

export default SelectPlatform;
